import {
	ErpUnitsPage,
	ErpUnitsPageProps,
} from "src/components/views/erp/warehouse/basedata/ErpUnitsPage.tsx";
import TestPage from "src/components/views/TestPage.tsx";
import i18n from "i18next";
import { PageTemplateProps } from "src/components/common/PageTemplate.tsx";
import { WarehouseLocationsPage } from "src/components/views/erp/warehouse/basedata/WarehouseLocationsPage.tsx";
import { SitesPage, SitesPageProps } from "src/components/views/erp/common/sites/SitesPage.tsx";
import {
	PartCategoriesPage,
	PartCategoriesPageProps,
} from "src/components/views/erp/parts/partCategories/PartCategoriesPage.tsx";
import {
	NumberSequencesPage,
	NumberSequencesPageProps,
} from "src/components/views/erp/common/numberSequences/NumberSequencesPage.tsx";
import { PurchaseRequestsPage } from "src/components/views/erp/purchase/PurchaseRequestsPage.tsx";
import {
	SystemDiagramsManagementPage,
	SystemDiagramsManagementPageProps,
} from "src/components/views/erp/common/systemDiagrams/SystemDiagramsManagementPage.tsx";
import { DocumentSearchPage } from "src/components/views/documents/search/DocumentSearchPage.tsx";
import {
	DocumentsOfObjectView,
	DocumentsOfObjectViewProps,
} from "src/components/views/documents/objectDocuments/DocumentsOfObjectView.tsx";
import { resolveHandlebarsTemplate } from "src/utils/handlebars.ts";
import { AavoObjectType } from "src/api/generated/common/sourceType/aavoObjectType.ts";
import { filterNulls } from "src/utils/arrays.ts";
import { FrontendViewConfig } from "src/components/views/frontendViews/frontendViewConfig.ts";
import { PickableCustomersOrdersDataGrid } from "src/components/views/erp/sales/picking/PickableCustomersOrdersDataGrid.tsx";
import { WarehousePickerGroupsDataGrid } from "../erp/warehouse/basedata/WarehousePickerGroupsDataGrid.tsx";
import {
	faBookmark,
	faBoxOpen,
	faClipboardList,
	faDiagramSubtask,
	faFile,
	faFileImport,
	faFileSignature,
	faFlag,
	faFolderOpen,
	faIndustry,
	faListAlt,
	faReceipt,
	faShapes,
	faShoppingCart,
	faSortNumericDown,
	faTable,
	faThumbtack,
	faTrailer,
	faTruckMoving,
	faUserFriends,
	faVial,
	faViruses,
	faWallet,
	faWarehouse,
} from "@fortawesome/pro-regular-svg-icons";
import { AllInvoicesDataGrid } from "../erp/sales/invoicing/AllInvoicesDataGrid.tsx";
import { MELLANO_VIEWS_BY_KEYS } from "src/tenantCustomizations/tenants/mellano/viewList.ts";
import { BillableCustomerOrdersDataGrid } from "src/components/views/erp/sales/invoicing/customerOrderInvoicing/BillableCustomerOrdersDataGrid.tsx";
import { CargoDriversDataGrid } from "../erp/warehouse/basedata/CargoDriversDataGrid.tsx";
import { DeliveryTermsDataGrid } from "../erp/warehouse/basedata/DeliveryTermsDataGrid.tsx";
import { DeliveryMethodDataGrid } from "../erp/warehouse/basedata/DeliveryMethodDataGrid.tsx";
import { ReclamationCausesDataGrid } from "src/components/views/erp/reclamation/baseData/ReclamationCausesDataGrid.tsx";
import { ReclamationCategoriesDataGrid } from "src/components/views/erp/reclamation/baseData/ReclamationCategoriesDataGrid.tsx";
import { CompaniesDataGrid } from "src/components/views/erp/common/companies/CompaniesDataGrid.tsx";
import { CustomerOrderImportPage } from "src/components/views/erp/sales/customerOrder/CustomerOrderImportPage.tsx";
import { AllTasksPage } from "src/components/views/tasks/pages/AllTasksPage.tsx";
import { AllReclamationsPage } from "../erp/reclamation/AllReclamationsPage.tsx";
import {
	SingleReclamationView,
	SingleReclamationViewProps,
} from "../erp/reclamation/SingleReclamationView.tsx";
import {
	ReclamationFromCustomerOrderForm,
	NewCustomerOrderReclamationFormProps,
} from "src/components/views/erp/reclamation/ReclamationFromCustomerOrderForm.tsx";
import { ReclamationForm, ReclamationFormProps } from "../erp/reclamation/ReclamationForm.tsx";
import { TasksAssignedToMePage } from "src/components/views/tasks/pages/TasksAssignedToMePage.tsx";
import { TasksOnMyResponsibilityPage } from "src/components/views/tasks/pages/TasksOnMyResponsibilityPage.tsx";

const defineFrontendView = <Props>(
	details: FrontendViewConfig<Props>,
): FrontendViewConfig<Props> => {
	return details;
};

export const FRONTEND_VIEWS_BY_KEYS: Record<string, FrontendViewConfig> = {
	...{
		devTestView: defineFrontendView<ErpUnitsPageProps>({
			component: TestPage,
			icon: faVial,
			displayName: i18n.t("test"),
			url: "/devTest",
		}),
		erpUnitsDataTableView: defineFrontendView<ErpUnitsPageProps>({
			component: ErpUnitsPage,
			displayName: i18n.t("units"),
			icon: faShapes,
			url: "/productManagement/units",
		}),
		warehouseLocationsPage: defineFrontendView<PageTemplateProps>({
			component: WarehouseLocationsPage,
			displayName: i18n.t("warehouse_locations"),
			icon: faWarehouse,
			url: "/warehouse/warehouseLocations",
		}),
		companiesDataTableView: defineFrontendView<unknown>({
			component: CompaniesDataGrid,
			displayName: i18n.t("companies"),
			icon: faIndustry,
			url: "/common/companies",
		}),
		sitesPage: defineFrontendView<SitesPageProps>({
			component: SitesPage,
			displayName: i18n.t("sites"),
			icon: faTable,
			url: "/common/sites",
		}),
		partCategoryManagementView: defineFrontendView<PartCategoriesPageProps>({
			component: PartCategoriesPage,
			displayName: i18n.t("part_categories"),
			icon: faBookmark,
			url: "/productManagement/partCategories",
		}),
		numberSequencesPage: defineFrontendView<NumberSequencesPageProps>({
			component: NumberSequencesPage,
			displayName: i18n.t("number_sequences"),
			icon: faSortNumericDown,
			url: "/common/numberSequences",
		}),
		purchaseRequestsPage: defineFrontendView<PageTemplateProps>({
			component: PurchaseRequestsPage,
			displayName: i18n.t("purchase_requests"),
			icon: faShoppingCart,
			url: "/purchase/purchaseRequests",
		}),
		systemDiagramsManagement: defineFrontendView<SystemDiagramsManagementPageProps>({
			component: SystemDiagramsManagementPage,
			displayName: i18n.t("system_diagrams"),
			icon: faDiagramSubtask,
			url: "/common/systemDiagrams",
		}),
		documentSearchPage: {
			component: DocumentSearchPage,
			displayName: i18n.t("documents"),
			icon: faFolderOpen,
			url: "/documents/search",
		},
		documentsOfObjectView: defineFrontendView<DocumentsOfObjectViewProps>({
			component: DocumentsOfObjectView,
			displayName: i18n.t("documents"),
			icon: faFile,
			modalSize: "fullscreen",
			mapViewParamsToProps: (params) => {
				const objectId = params["objectId"]!.toString();
				const objectTypeStr = params["objectType"] as string;
				const readonly = params["readonly"] === true;
				return {
					objectRef: {
						objectType: resolveHandlebarsTemplate(
							objectTypeStr,
							params,
						) as AavoObjectType,
						objectId: Number(resolveHandlebarsTemplate(objectId, params)),
					},
					editable: !readonly,
				};
			},
		}),
		pickableCustomersOrdersDataGrid: {
			component: PickableCustomersOrdersDataGrid,
			displayName: i18n.t("pickable_customer_orders"),
			icon: faBoxOpen,
			url: "/sales/picking/pickableCustomerOrders",
			modalSize: "fullscreen",
		},
		warehousePickerGroupsPage: {
			component: WarehousePickerGroupsDataGrid,
			displayName: i18n.t("picker_groups"),
			icon: faUserFriends,
			url: "/erp/warehouse/warehousePickerGroups",
		},
		allInvoicesPage: {
			component: AllInvoicesDataGrid,
			displayName: i18n.t("all_invoices"),
			icon: faWallet,
			url: "/erp/sales/invoicing/allInvoices",
		},
		billableCustomerOrdersPage: {
			component: BillableCustomerOrdersDataGrid,
			displayName: i18n.t("billable_customer_orders"),
			icon: faReceipt,
			url: "/erp/sales/invoicing/billableCustomerOrders",
		},
		cargoDriversDataGrid: {
			component: CargoDriversDataGrid,
			displayName: i18n.t("cargo_drivers"),
			icon: faTruckMoving,
			url: "/erp/warehouse/cargoDrivers",
		},
		deliveryTermsDataGrid: {
			component: DeliveryTermsDataGrid,
			displayName: i18n.t("delivery_terms"),
			icon: faFileSignature,
			url: "/erp/warehouse/deliveryTerms",
		},
		deliveryMethodDataGrid: {
			component: DeliveryMethodDataGrid,
			displayName: i18n.t("delivery_methods"),
			icon: faTrailer,
			url: "/erp/warehouse/deliveryMethods",
		},
		reclamationCausesPage: {
			component: ReclamationCausesDataGrid,
			displayName: i18n.t("reclamation_causes"),
			icon: faViruses,
			url: "/erp/reclamations/reclamationCause",
		},
		customerOrderImportPage: {
			component: CustomerOrderImportPage,
			displayName: i18n.t("import_customer_orders"),
			icon: faFileImport,
			url: "/erp/sales/customerOrderImport",
		},
		reclamationCategoriesPage: {
			component: ReclamationCategoriesDataGrid,
			displayName: i18n.t("reclamation_categories"),
			icon: faShapes,
			url: "/erp/reclamations/reclamationCategory",
		},
		allReclamationsPage: {
			component: AllReclamationsPage,
			displayName: i18n.t("reclamations"),
			icon: faFlag,
			url: "/erp/reclamations/reclamations",
		},
		singleReclamationView: defineFrontendView<SingleReclamationViewProps>({
			icon: faFlag,
			component: SingleReclamationView,
			displayName: i18n.t("reclamation"),
			mapViewParamsToProps: (params) => {
				const reclamationId = params["selectedReclamationId"]!.toString();
				return { reclamationId: Number(resolveHandlebarsTemplate(reclamationId, params)) };
			},
		}),
		allTasksPage: {
			component: AllTasksPage,
			displayName: i18n.t("all_tasks"),
			icon: faClipboardList,
			url: "/tasks/allTasks",
		},
		newCustomerOrderReclamationForm: defineFrontendView<NewCustomerOrderReclamationFormProps>({
			component: ReclamationFromCustomerOrderForm,
			displayName: i18n.t("reclamation"),
			icon: faFlag,
			modalSize: "md",
			mapViewParamsToProps: (params, closeModal) => {
				const customerOrderId = params["customerOrderId"]!.toString();
				return {
					customerOrderId: Number(resolveHandlebarsTemplate(customerOrderId, params)),
					closeModal: closeModal,
				};
			},
		}),
		reclamationForm: defineFrontendView<ReclamationFormProps>({
			component: ReclamationForm,
			displayName: i18n.t("reclamation"),
			icon: faFlag,
			modalSize: "md",
			mapViewParamsToProps: (params, closeModal) => {
				const reclamationId = params["reclamationId"]!.toString();
				return {
					reclamationId: Number(resolveHandlebarsTemplate(reclamationId, params)),
					closeModal,
				};
			},
		}),
		tasksAssignedToMePage: {
			component: TasksAssignedToMePage,
			displayName: i18n.t("own_tasks"),
			icon: faThumbtack,
			url: "/tasks/my",
		},
		tasksOnMyResponsibilityPage: {
			component: TasksOnMyResponsibilityPage,
			displayName: i18n.t("on_my_responsibility"),
			icon: faListAlt,
			url: "/tasks/onMyResponsibility",
		},
	},
	...MELLANO_VIEWS_BY_KEYS,
};

export const FRONTEND_VIEWS_LIST = Object.values(FRONTEND_VIEWS_BY_KEYS);

export const FRONTEND_VIEW_ENTRIES = Object.entries(FRONTEND_VIEWS_BY_KEYS);

export const FRONTEND_PATHS: string[] = filterNulls(
	FRONTEND_VIEWS_LIST.map((frontendView) => frontendView.url),
);
